import { Alert, Spin } from 'antd';
import React, { useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { PreviewToolbar, usePreviewScale } from '../PreviewToolbar';
import { useDocxDisclaimer } from './useDocxDisclaimer';
import { useDocxFrame } from './useDocxFrame';
import { useDocxPages } from './useDocxPages';
import { useDocxPreview } from './useDocxPreview';
import { DOCX_DEFAULT_DISCLAIMER, buildSrcDoc } from './utils';

export interface IDocxPreviewProps {
  url: string;
  fileName?: string;
  /** Controlled zoom — supply both to sync zoom across linked previews. */
  scale?: number;
  onScaleChange?: (scale: number) => void;
  /** Text of the dismissible warning banner. Defaults to a built-in message. */
  disclaimer?: string;
}

const DocxPreview: React.FC<IDocxPreviewProps> = ({
  url,
  fileName = 'Document preview',
  scale: scaleProp,
  onScaleChange,
  disclaimer = DOCX_DEFAULT_DISCLAIMER,
}) => {
  const theme = useTheme();
  const { renderPreview, sanitizedDocx, isRenderingPreview, errorMessage } =
    useDocxPreview();
  const { iframeRef, frameDoc, handleFrameLoad } = useDocxFrame();
  const { scale, setScale } = usePreviewScale(scaleProp, onScaleChange);
  const { numPages, currentPage, scrollToPage } = useDocxPages(frameDoc, scale);
  const { isDisclaimerVisible, dismissDisclaimer } = useDocxDisclaimer();

  const srcDoc = useMemo(
    () => (sanitizedDocx ? buildSrcDoc(sanitizedDocx, theme) : null),
    [sanitizedDocx, theme]
  );

  useEffect(() => {
    renderPreview(url);
  }, [url, renderPreview]);

  if (isRenderingPreview) {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    );
  }

  if (errorMessage) {
    return <Centered role="alert">{errorMessage}</Centered>;
  }

  if (!srcDoc) return null;

  return (
    <Wrapper>
      <PreviewToolbar
        scale={scale}
        onScaleChange={setScale}
        downloadUrl={url}
        fileName={fileName}
        currentPage={currentPage}
        numPages={numPages}
        onPageChange={scrollToPage}
      />
      {isDisclaimerVisible && (
        <Alert
          banner
          closable
          type="warning"
          message={disclaimer}
          onClose={dismissDisclaimer}
        />
      )}
      <Frame
        ref={iframeRef}
        title={fileName}
        // Never add allow-scripts next to allow-same-origin: it voids the sandbox.
        sandbox="allow-same-origin"
        referrerPolicy="no-referrer"
        srcDoc={srcDoc}
        onLoad={handleFrameLoad}
      />
    </Wrapper>
  );
};

export default DocxPreview;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Frame = styled.iframe`
  display: block;
  flex: 1;
  min-height: 0;
  width: 100%;
  border: none;
`;
